// @ts-check
import React, { useContext, useEffect, useReducer, useState } from 'react';

// Core.
import {
  ANNOUNCEMENT_CONTENT_MIN_LENGTH,
  ANNOUNCEMENT_TITLE_MIN_LENGTH,
  AuthContext,
  BubbleLoader,
  ERROR_ANNOUNCEMENT_CONTENT_MIN_LENGTH,
  ERROR_ANNOUNCEMENT_TITLE_MIN_LENGTH,
  RequiredIcon,
  scrollToElement,
} from 'core';
import { useAppDispatch, useAppSelector } from 'core/store';
import { updateAnnouncementAsync } from 'core/store/slices';

// Local components.
import { AnnouncementContent } from './components/AnnouncementContent';
import { AnnouncementDate } from './components/AnnouncementDate';
import { AnnouncementHistory } from './components/AnnouncementHistory';
import { AnnouncementTitle } from './components/AnnouncementTitle';

/** @type {AnnouncementCreateErrors} */
const initStateErrors = {
  announcementTitle: '',
  announcementContent: '',
  dateRange: '',
  otherErrors: '',
};

/** @param {AnnouncementCreateErrors} errors */
const isValid = (errors) => {
  return Object.values(errors).every((val) => val.length === 0);
};

/** @param {string []} errors */
const parseErrors = (errors) => errors.filter((err) => err);

/**
 * @param {Partial<AnnouncementModel>} state
 * @param {Partial<AnnouncementModel>} payload
 */
const handleFormReducer = (state, payload) => ({
  ...state,
  ...payload,
});

const DataForm = () => {
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const { announcement, loading: announcementLoading } = useAppSelector(
    (state) => state.announcementData,
  );

  const initialFormData = {
    content: announcement.content,
    title: announcement.title,
    endAt: announcement.endAt,
    startAt: announcement.startAt,
    lastUpdatedBy: announcement.lastUpdatedBy,
    updatedAt: announcement.updatedAt,
  };

  /** @type {[AnnouncementCreateErrors, React.Dispatch<React.SetStateAction<AnnouncementCreateErrors>>]} */
  const [errors, setErrors] = useState({ ...initStateErrors });

  /**
   * @param {ChangeEvent<HTMLInputElement>} e
   * @param {AnnouncementCreateErrors} [prevErrors]
   */
  const checkErrors = (e, prevErrors = initStateErrors) => {
    const { name, value } = e.target;

    let nextErrors = prevErrors;

    setErrors((currentErrors) => {
      const newErrors = {
        ...prevErrors,
        otherErrors: '',
      };

      if (name === 'title') {
        const titleLength = String(value).trim().length;
        if (titleLength < ANNOUNCEMENT_TITLE_MIN_LENGTH) {
          newErrors.announcementTitle = ERROR_ANNOUNCEMENT_TITLE_MIN_LENGTH;
        } else {
          newErrors.announcementTitle = '';
        }
      } else if (name === 'content') {
        // Strip HTML tags for validation
        const textContent = String(value)
          .replace(/<[^>]*>/g, '')
          .trim();
        const contentLength = textContent.length;
        if (contentLength < ANNOUNCEMENT_CONTENT_MIN_LENGTH) {
          newErrors.announcementContent = ERROR_ANNOUNCEMENT_CONTENT_MIN_LENGTH;
        } else {
          newErrors.announcementContent = '';
        }
      }

      nextErrors = newErrors;
      return newErrors;
    });

    return nextErrors;
  };

  const saveAnnouncement = async (e) => {
    e.preventDefault();

    // Validate all fields before saving
    let newErrors = { ...errors };
    let firstInvalidElement;

    /** @param {string} id */
    const setFirstInvalidElement = (id) => {
      if (!firstInvalidElement) {
        firstInvalidElement = `#${id}-field`;
      }
    };

    // Validate title
    const titleEvent = /** @type {ChangeEvent<HTMLInputElement>} */ ({
      target: {
        name: 'title',
        value: formData.title,
      },
    });
    newErrors = checkErrors(titleEvent, newErrors);

    if (newErrors.announcementTitle) {
      setFirstInvalidElement('announcement-title');
    }

    // Validate content
    const contentEvent = /** @type {ChangeEvent<HTMLInputElement>} */ ({
      target: {
        name: 'content',
        value: formData.content,
      },
    });
    newErrors = checkErrors(contentEvent, newErrors);

    if (newErrors.announcementContent) {
      setFirstInvalidElement('announcement-content');
    }

    // Validate date range
    newErrors = validateDateRange(formData.startAt, formData.endAt, newErrors);

    if (newErrors.dateRange) {
      setFirstInvalidElement('announcement-endAt');
    }

    if (firstInvalidElement) {
      scrollToElement(firstInvalidElement, true);
    }

    if (!isValid(newErrors)) {
      return;
    }

    console.log('saveAnnouncement', formData);

    const saveActionAsync = announcement?.announcementId
      ? updateAnnouncementAsync
      : updateAnnouncementAsync; //TODO: Add createAnnouncementAsync.

    dispatch(
      saveActionAsync({ announcement: { ...formData, lastUpdatedBy: user } }),
    );
  };

  const [formData, setFormData] = useReducer(handleFormReducer, {
    content: announcement.content,
    title: announcement.title,
    endAt: announcement.endAt,
    startAt: announcement.startAt,
    lastUpdatedBy: announcement.lastUpdatedBy,
    updatedAt: announcement.updatedAt,
  });

  // Track the current announcement ID to detect when a new one is loaded
  const [currentAnnouncementId, setCurrentAnnouncementId] = useState(
    announcement.announcementId,
  );

  useEffect(() => {
    setFormData({
      ...announcement,
    });
    // Only clear errors when loading a different announcement
    if (announcement.announcementId !== currentAnnouncementId) {
      setErrors({ ...initStateErrors });
      setCurrentAnnouncementId(announcement.announcementId);
    }
  }, [announcement, currentAnnouncementId]);

  const htmlChange = (e) => {
    const contentEvent = /** @type {ChangeEvent<HTMLInputElement>} */ ({
      target: {
        name: 'content',
        value: e.target.value,
      },
    });
    checkErrors(contentEvent, errors);
    setFormData({ content: e.target.value });
  };

  /**
   * Pure function to validate date range
   * @param {Date | string} startDate
   * @param {Date | string} endDate
   * @param {AnnouncementCreateErrors} currentErrors
   * @returns {AnnouncementCreateErrors}
   */
  const validateDateRange = (startDate, endDate, currentErrors) => {
    const newErrors = { ...currentErrors };
    newErrors.dateRange = '';

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (end < start) {
        newErrors.dateRange = 'End date must be on or after the start date.';
      }
    }

    return newErrors;
  };

  const startDateChange = (date) => {
    setFormData({ startAt: date });
    setErrors((prevErrors) =>
      validateDateRange(date, formData.endAt, prevErrors),
    );
  };

  const endDateChange = (date) => {
    setFormData({ endAt: date });
    setErrors((prevErrors) =>
      validateDateRange(formData.startAt, date, prevErrors),
    );
  };

  const inputChange = (e) => {
    const { name, value } = e.target;

    checkErrors(e, errors);

    /** @type {any} */
    setFormData({ [name]: value });
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({ ...initStateErrors });
  };

  const classFirstCol = 'col-md-4';

  return (
    <form className="uds-form" noValidate method="post">
      <div className="container p-0">
        {announcementLoading ? (
          <BubbleLoader />
        ) : (
          <>
            <header className={`${classFirstCol} mb-3`}>
              <label>
                <RequiredIcon />
                <span className="font-weight-normal">Required fields</span>
              </label>
            </header>
            <section className={classFirstCol}>
              <AnnouncementTitle
                announcementTitle={formData.title}
                errors={parseErrors([errors.announcementTitle])}
                readonly={false}
                onChange={inputChange}
              />
            </section>
            <section className={classFirstCol}>
              <AnnouncementContent
                announcementContent={formData.content}
                errors={parseErrors([errors.announcementContent])}
                readonly={false}
                onChange={htmlChange}
              />
            </section>
            <section>
              <div className="container">
                <div className="row">
                  <div className="col-md-4">
                    <AnnouncementDate
                      dateId="startAt"
                      dateName="startAt"
                      announcementDate={formData.startAt}
                      dateTitle="Start Date"
                      tooltip="The date when the announcement becomes visible to users."
                      errors={[]}
                      readOnly={false}
                      onChange={startDateChange}
                    />
                  </div>
                </div>
              </div>
            </section>
            <section>
              <div className="container">
                <div className="row">
                  <div className="col-md-4">
                    <AnnouncementDate
                      dateId="endAt"
                      dateName="endAt"
                      announcementDate={formData.endAt}
                      dateTitle="End Date"
                      tooltip="The date when the announcement stops being visible. Must be on or after the start date."
                      errors={parseErrors([errors.dateRange])}
                      readOnly={false}
                      onChange={endDateChange}
                    />
                  </div>
                </div>
              </div>
            </section>
            <section>
              <div className="container">
                <div className="row">
                  <div className="col-md-4">
                    <button
                      className="btn btn-gray"
                      type="button"
                      aria-describedby="cancel-announcemnt"
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-maroon ms-3"
                      type="button"
                      aria-describedby="save-announcement-tooltip"
                      onClick={saveAnnouncement}
                    >
                      Save Announcement
                    </button>
                  </div>
                </div>
              </div>
            </section>
            <section>
              <div className="container">
                <div className="row">
                  <div className="col-md-4">
                    <AnnouncementHistory
                      createdBy={announcement.createdBy}
                      createdAt={announcement.createdAt}
                      lastUpdatedBy={announcement.lastUpdatedBy}
                      updatedAt={announcement.updatedAt}
                    />
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </form>
  );
};

export { DataForm };
